import { ALL_DOMAINS, MasteryThreshold } from "./config";
import { masteryForPercentage } from "./scoring";
import {
  AnswerOutcome,
  MasteryState,
  type DomainProgress,
  type ProgressEvent,
  type ProgressSummary,
} from "./types";

export function summarizeProgress(
  events: readonly ProgressEvent[],
): ProgressSummary {
  if (events.length === 0) {
    return {
      attemptCount: 0,
      latestPercentage: null,
      overallPercentage: null,
      overallMastery: MasteryState.NOT_ASSESSED,
      domains: [],
    };
  }

  const allResults = events.flatMap((event) => event.report.results);
  const domains = ALL_DOMAINS.flatMap((domain): readonly DomainProgress[] => {
    const domainResults = allResults.filter((result) => result.domain === domain);
    if (domainResults.length === 0) {
      return [];
    }

    const correct = domainResults.filter(
      (result) => result.outcome === AnswerOutcome.CORRECT,
    ).length;
    const percentage = Math.round((correct / domainResults.length) * 100);

    return [
      {
        domain,
        correct,
        total: domainResults.length,
        percentage,
        mastery: masteryForPercentage(percentage),
      },
    ];
  });

  const totalCorrect = allResults.filter(
    (result) => result.outcome === AnswerOutcome.CORRECT,
  ).length;
  const overallPercentage = Math.round((totalCorrect / allResults.length) * 100);
  const allDomainsReady =
    domains.length === ALL_DOMAINS.length &&
    domains.every(
      (domain) => domain.percentage >= MasteryThreshold.READY_DOMAIN_MINIMUM,
    );

  return {
    attemptCount: events.length,
    latestPercentage: events.at(-1)?.report.percentage ?? null,
    overallPercentage,
    overallMastery:
      overallPercentage >= MasteryThreshold.READY_MINIMUM
        ? allDomainsReady
          ? MasteryState.READY
          : MasteryState.PROFICIENT
        : masteryForPercentage(overallPercentage),
    domains,
  };
}

export function weakestDomains(
  summary: ProgressSummary,
  maximumCount: number,
): readonly DomainProgress[] {
  if (maximumCount <= 0) {
    return [];
  }

  return [...summary.domains]
    .sort(
      (left, right) =>
        left.percentage - right.percentage ||
        left.domain.localeCompare(right.domain),
    )
    .slice(0, maximumCount);
}
